import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable/index";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import { useDeviceSession, DeviceSession } from "@/hooks/useDeviceSession";
import { DeviceLimitModal } from "./DeviceLimitModal";
import { ArrowLeft, Mail, RefreshCw } from "lucide-react";

const KEEP_SIGNED_IN_KEY = "hockey_keep_signed_in";

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultMode?: "signin" | "signup";
}

type SignInStep = "email" | "magic-link-sent";

const AuthModal = ({ isOpen, onClose, defaultMode = "signin" }: AuthModalProps) => {
  const [mode, setMode] = useState<"signin" | "signup" | "forgot">(defaultMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [signInStep, setSignInStep] = useState<SignInStep>("email");
  
  const [keepSignedIn, setKeepSignedIn] = useState(true);
  const [showDeviceLimit, setShowDeviceLimit] = useState(false);
  const [existingSessions, setExistingSessions] = useState<DeviceSession[]>([]);
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);
  const { t } = useLanguage();
  const { canRegisterDevice, registerDevice, removeDevice } = useDeviceSession();

  const saveKeepSignedInPref = () => {
    localStorage.setItem(KEEP_SIGNED_IN_KEY, keepSignedIn ? "true" : "false");
    sessionStorage.setItem("hockey_session_alive", "1");
  };

  const handleSocialLogin = async (provider: "google" | "apple") => {
    setIsLoading(true);
    try {
      saveKeepSignedInPref();
      const { error } = await lovable.auth.signInWithOAuth(provider, {
        redirect_uri: window.location.origin,
      });
      if (error) throw error;
    } catch (error: any) {
      toast.error(error.message || `Failed to sign in with ${provider}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeviceCheck = async (userId: string): Promise<boolean> => {
    const { allowed, existingSessions, isCurrentDeviceRegistered } = await canRegisterDevice(userId);
    
    if (!allowed) {
      setExistingSessions(existingSessions);
      setPendingUserId(userId);
      setShowDeviceLimit(true);
      return false;
    }

    if (!isCurrentDeviceRegistered) {
      await registerDevice(userId);
    }
    return true;
  };

  const handleRetryAfterRemove = async () => {
    if (!pendingUserId) return;
    
    const deviceAllowed = await handleDeviceCheck(pendingUserId);
    if (deviceAllowed) {
      setShowDeviceLimit(false);
      toast.success("Signed in successfully!");
      onClose();
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter your email address");
      return;
    }
    
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth?type=recovery`,
      });
      
      if (error) throw error;
      
      toast.success("Password reset email sent! Check your inbox.");
      setMode("signin");
    } catch (error: any) {
      toast.error(error.message || "Failed to send reset email");
    } finally {
      setIsLoading(false);
    }
  };

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter your email address");
      return;
    }
    
    setIsLoading(true);
    try {
      saveKeepSignedInPref();

      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/dashboard`,
        },
      });
      
      if (error) throw error;
      
      setSignInStep("magic-link-sent");
      toast.success("Link sent! Check your inbox.");
    } catch (error: any) {
      toast.error(error.message || "Failed to send link");
    } finally {
      setIsLoading(false);
    }
  };


  const handleResendLink = async () => {
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: `${window.location.origin}/dashboard` },
      });
      
      if (error) throw error;
      
      toast.success("New link sent!");
    } catch (error: any) {
      toast.error(error.message || "Failed to resend");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: window.location.origin,
        },
      });
      if (error) throw error;
      
      if (data.user) {
        await registerDevice(data.user.id);
      }
      
      toast.success("Account created successfully!");
      onClose();
    } catch (error: any) {
      toast.error(error.message || "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  const getTitle = () => {
    if (mode === "forgot") return "Reset Password";
    if (mode === "signin") {
      if (signInStep === "magic-link-sent") return "Check Your Email";
      return t.nav.signIn;
    }
    return "Create Account";
  };

  const resetState = () => {
    setSignInStep("email");
    setEmail("");
    setPassword("");
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      if (!open) {
        resetState();
      }
      onClose();
    }}>
      <DialogContent className="w-[90vw] max-w-md bg-card border-border left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%]">
        <DialogHeader>
          <DialogTitle className="text-2xl font-display text-center">
            {getTitle()}
          </DialogTitle>
        </DialogHeader>
        
        {mode === "forgot" ? (
          <form onSubmit={handleForgotPassword} className="space-y-4 mt-4">
            <p className="text-sm text-muted-foreground text-center">
              Enter your email address and we'll send you a link to reset your password.
            </p>
            <div className="space-y-2">
              <Label htmlFor="reset-email">Email</Label>
              <Input
                id="reset-email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-background"
              />
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? "Sending..." : "Send Reset Link"}
            </Button>
            
            <button
              type="button"
              onClick={() => setMode("signin")}
              className="flex items-center justify-center gap-1 w-full text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Sign In
            </button>
          </form>
        ) : mode === "signin" ? (
          <>
            {signInStep === "magic-link-sent" ? (
              <div className="space-y-4 mt-4 text-center">
                <div className="w-16 h-16 mx-auto bg-primary/10 rounded-full flex items-center justify-center">
                  <Mail className="w-8 h-8 text-primary" />
                </div>
                <p className="text-muted-foreground">
                  We've sent a link to <span className="font-medium text-foreground">{email}</span>
                </p>
                <p className="text-sm text-muted-foreground">
                  Click the link in the email to sign in instantly. No password needed!
                </p>
                
                <div className="bg-muted/50 p-3 rounded-lg text-sm text-muted-foreground">
                  <p>📧 Don't see it? Check your spam folder.</p>
                </div>

                <Button
                  variant="outline"
                  onClick={handleResendLink}
                  disabled={isLoading}
                  className="w-full gap-2"
                >
                  <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                  {isLoading ? "Sending..." : "Resend Link"}
                </Button>

                <button
                  type="button"
                  onClick={() => {
                    setSignInStep("email");
                    setEmail("");
                  }}
                  className="flex items-center justify-center gap-1 w-full text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Use a different email
                </button>
              </div>
            ) : (
              <form onSubmit={handleMagicLink} className="space-y-4 mt-4">
                <p className="text-sm text-muted-foreground text-center">
                  Enter your email to sign in – we'll send you a magic link.
                </p>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="bg-background"
                  />
                </div>

                <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50 border border-border/50">
                  <Checkbox
                    id="keep-signed-in"
                    checked={keepSignedIn}
                    onCheckedChange={(checked) => setKeepSignedIn(checked === true)}
                    className="mt-0.5"
                  />
                  <label htmlFor="keep-signed-in" className="text-sm cursor-pointer select-none">
                    <span className="font-medium text-foreground">Keep me signed in on this device</span>
                    <span className="block text-xs text-muted-foreground mt-0.5">
                      Stay signed in for 30 days – no new link needed next time on this device.
                    </span>
                  </label>
                </div>

                <Button
                  type="submit"
                  className="w-full gap-2"
                  disabled={isLoading}
                >
                  <Mail className="w-4 h-4" />
                  {isLoading ? "Sending..." : "Send Link"}
                </Button>

                <div className="relative my-1">
                  <div className="absolute inset-0 flex items-center">
                    <Separator className="w-full" />
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="bg-card px-2 text-muted-foreground">or</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground text-center">
                    Sign in with Google / Apple – faster return visits
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      className="gap-2"
                      disabled={isLoading}
                      onClick={() => handleSocialLogin("google")}
                    >
                      <svg className="w-4 h-4" viewBox="0 0 24 24">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                      </svg>
                      Google
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="gap-2"
                      disabled={isLoading}
                      onClick={() => handleSocialLogin("apple")}
                    >
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                      </svg>
                      Apple
                    </Button>
                  </div>
                </div>

                <div className="text-center text-sm text-muted-foreground">
                  <p>
                    Don't have an account?{" "}
                    <button
                      type="button"
                      onClick={() => setMode("signup")}
                      className="text-primary hover:underline font-medium"
                    >
                      Sign up
                    </button>
                  </p>
                </div>
              </form>
            )}
          </>
        ) : (
          <>
            <form onSubmit={handleSignup} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-background"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="bg-background"
                />
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={isLoading}
              >
                {isLoading ? "Creating account..." : "Sign Up"}
              </Button>
            </form>

            <div className="text-center text-sm text-muted-foreground mt-4">
              <p>
                Already have an account?{" "}
                <button
                  type="button"
                  onClick={() => setMode("signin")}
                  className="text-primary hover:underline font-medium"
                >
                  Sign in
                </button>
              </p>
            </div>
          </>
        )}

        <DeviceLimitModal
          isOpen={showDeviceLimit}
          onClose={async () => {
            setShowDeviceLimit(false);
            setPendingUserId(null);
            await supabase.auth.signOut();
          }}
          sessions={existingSessions}
          onRemoveDevice={removeDevice}
          onRetry={handleRetryAfterRemove}
        />
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;
